import ChevronLeftIcon from '@/assets/icons/chevron-left.svg'
import ChevronRightIcon from '@/assets/icons/chevron-right.svg'
import { cn } from '@/lib/utils'
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type Column,
  type ColumnDef,
  type OnChangeFn,
  type RowData,
  type RowSelectionState,
  type SortingState,
} from '@tanstack/react-table'
import ArrowUpwardAltIcon from '@/assets/icons/arrow_upward_alt.svg'
import ArrowDownwardAltIcon from '@/assets/icons/arrow_downward_alt.svg'
import SwapVertIcon from '@/assets/icons/swap_vert.svg'
import { MoreVertical } from 'lucide-react'
import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { ListMenuItem } from '@/components/ui/list-menu'
import { SimpleSelect } from '@/components/ui/select'

export interface PaginationInfo {
  page: number
  totalPages: number
  totalItems: number
  pageSize: number
  onPageChange: (page: number) => void
  showGoToInput?: boolean
  showResultLabel?: boolean
}

export interface BaseTableProps<TData extends RowData> {
  data: TData[]
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: ColumnDef<TData, any>[]
  rowActions?: (row: TData) => RowAction[]
  pagination?: PaginationInfo
  emptyMessage?: { title: string; description: string } | string
  className?: string
  popoverContentClassName?: string
  hideIndex?: boolean
  pinningLeftColumns?: string[] // array of column ids that should be pinned to the left
  pinningRightColumns?: string[] // array of column ids that should be pinned to the right
  handleRowClick?: (row: TData) => void
  searchHighlight?: string
  searchHighlightColumns?: string[]
  sorting?: SortingState
  onSortingChange?: OnChangeFn<SortingState>
  manualSorting?: boolean
  enableRowSelection?: boolean
  onSelectedRowsChange?: (rows: TData[]) => void
}

export interface RowAction {
  label: string
  onClick: () => void
  icon?: ReactNode
  variant?: 'primary' | 'secondary' | 'ghost' | 'link' | 'danger'
  danger?: boolean
  dividerBefore?: boolean
  disabled?: boolean
}

const ROW_ACTIONS_COLUMN_WIDTH = 48

const pinnedRowActionsStyles: CSSProperties = {
  position: 'sticky',
  right: 0,
  width: ROW_ACTIONS_COLUMN_WIDTH,
  minWidth: ROW_ACTIONS_COLUMN_WIDTH,
  maxWidth: ROW_ACTIONS_COLUMN_WIDTH,
}

const getProvidedColSize = <TData extends RowData>(
  targetColumn: Column<TData, unknown>,
  allColumns: ColumnDef<TData, unknown>[],
) => {
  const column = allColumns.find((column) => column.header === targetColumn.columnDef.header)
  return {
    size: column?.size,
    minSize: column?.minSize,
    maxSize: column?.maxSize,
  }
}

const getCommonPinningStyles = <TData extends RowData>(
  column: Column<TData>,
  allColumns: ColumnDef<TData, unknown>[],
  rightOffset = 0,
): CSSProperties => {
  const isPinned = column.getIsPinned()
  // const renderSize = column.getSize()
  const defineSize = getProvidedColSize(column, allColumns)

  return {
    left: isPinned === 'left' ? `${column.getStart('left')}px` : undefined,
    right: isPinned === 'right' ? `${column.getAfter('right') + rightOffset}px` : undefined,
    opacity: isPinned ? 0.98 : 1,
    position: isPinned ? 'sticky' : 'relative',
    // Enforce exact width to fix TanStack Table issue #5683 where pinned column
    // left offsets are computed from declared size but actual rendered columns
    // can be wider than declared, causing gaps/overlap between sticky columns.
    // https://github.com/TanStack/table/issues/5683
    width: defineSize.size ?? 'auto',
    minWidth: defineSize.minSize ?? defineSize.size,
    maxWidth: defineSize.maxSize ?? defineSize.size,
    zIndex: isPinned ? 1 : 0,
  }
}

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

export function HighlightedText({ text, highlight }: { text: string | number; highlight?: string }) {
  const textValue = String(text)
  const highlightValue = highlight?.trim()

  if (!highlightValue) return textValue

  const pattern = new RegExp(`(${escapeRegExp(highlightValue)})`, 'gi')

  return textValue.split(pattern).map((part, index) =>
    part.toLocaleLowerCase() === highlightValue.toLocaleLowerCase() ? (
      <span key={`${part}-${index}`} className="text-main-text-display">
        {part}
      </span>
    ) : (
      part
    ),
  )
}

function TextHighlightCell({
  value,
  highlight,
  columnId,
  highlightColumns,
}: {
  value: unknown
  highlight?: string
  columnId: string
  highlightColumns?: string[]
}) {
  const shouldHighlight = highlightColumns && highlightColumns.includes(columnId)

  if (typeof value === 'string' || typeof value === 'number') {
    return shouldHighlight ? <HighlightedText text={value} highlight={highlight} /> : String(value)
  }

  return null
}

export function BaseTable<TData extends RowData>({
  data,
  columns,
  rowActions,
  pagination,
  emptyMessage = 'No data',
  className,
  popoverContentClassName,
  hideIndex,
  pinningLeftColumns,
  pinningRightColumns,
  handleRowClick = undefined,
  searchHighlight,
  searchHighlightColumns,
  sorting,
  onSortingChange,
  manualSorting = true,
  enableRowSelection = false,
  onSelectedRowsChange,
}: BaseTableProps<TData>) {
  const searchHighlightRef = useRef(searchHighlight)
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  searchHighlightRef.current = searchHighlight
  const pinRowActions = Boolean(rowActions && pinningRightColumns?.length)
  const rightPinningOffset = pinRowActions ? ROW_ACTIONS_COLUMN_WIDTH : 0

  useEffect(() => {
    setRowSelection({})
  }, [data])

  const table = useReactTable({
    data,
    columns,
    defaultColumn: {
      cell: (info) => (
        <TextHighlightCell
          value={info.getValue()}
          highlight={searchHighlightRef.current}
          columnId={info.column.id}
          highlightColumns={searchHighlightColumns}
        />
      ),
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualSorting,
    enableSorting: Boolean(sorting && onSortingChange),
    enableRowSelection,
    enableMultiSort: true,
    isMultiSortEvent: () => true,
    state: {
      sorting,
      rowSelection,
    },
    onSortingChange,
    onRowSelectionChange: setRowSelection,
    initialState: {
      columnPinning: {
        left: pinningLeftColumns ?? [],
        right: pinningRightColumns ?? [],
      },
    },
  })

  useEffect(() => {
    onSelectedRowsChange?.(table.getSelectedRowModel().rows.map((row) => row.original))
  }, [onSelectedRowsChange, rowSelection, table])

  const tableContent = (
    <div
      className={cn(
        'overflow-x-auto rounded-2xl border border-main-text-lv03 bg-creation-neutral-container-surface-default',
        data.length === 0 && 'flex flex-1 flex-col',
      )}
    >
      <table
        className={cn(
          'border-collapse bg-creation-neutral-container-surface-default table-auto min-w-full',
          data.length === 0 && 'flex-1 [&_thead]:h-px',
        )}
      >
        <thead className="bg-main-surface-lv-02-dim text-left text-ui-title-t2 leading-ui-title-t2 font-semibold text-main-text-lv01 **:[th,tr]:min-h-12 **:[th]:border-b **:[th]:border-main-text-lv03 **:[th,tr]:p-2">
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {!hideIndex && <th className="w-10 text-left">#</th>}
              {enableRowSelection && (
                <th className="w-14">
                  <Checkbox
                    checked={table.getIsAllPageRowsSelected()}
                    indeterminate={table.getIsSomePageRowsSelected()}
                    onCheckedChange={(checked) => table.toggleAllPageRowsSelected(Boolean(checked))}
                    aria-label="Select all rows"
                  />
                </th>
              )}
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  aria-sort={
                    header.column.getIsSorted() === 'asc'
                      ? 'ascending'
                      : header.column.getIsSorted() === 'desc'
                        ? 'descending'
                        : undefined
                  }
                  className={cn(
                    'bg-main-surface-lv-02-dim align-middle',
                    header.column.getIsPinned() === 'left' && header.column.getIsLastColumn('left')
                      ? "after:content-[''] after:absolute after:right-0 after:top-0 after:bottom-0 after:w-px after:bg-main-border-lv02 after:z-10"
                      : '',
                    header.column.getIsPinned() === 'right' && header.column.getIsFirstColumn('right')
                      ? "after:content-[''] after:absolute after:left-0 after:top-0 after:bottom-0 after:w-px after:bg-main-border-lv02 after:z-10"
                      : '',
                    header.column.columnDef.meta?.className,
                  )}
                  style={{
                    ...getCommonPinningStyles(header.column, columns, rightPinningOffset),
                  }}
                >
                  <div
                    className={cn(
                      'inline-flex items-center text-left text-ui-title-t2 leading-ui-title-t2 font-semibold text-main-text-lv01',
                      header.column.getCanSort() ? 'cursor-pointer' : '',
                    )}
                  >
                    <span className="px-1.5 py-0.5">
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </span>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      onClick={header.column.getToggleSortingHandler()}
                      disabled={!header.column.getCanSort()}
                    >
                      {header.column.getCanSort() &&
                        (header.column.getIsSorted() === 'asc' ? (
                          <ArrowUpwardAltIcon />
                        ) : header.column.getIsSorted() === 'desc' ? (
                          <ArrowDownwardAltIcon />
                        ) : (
                          <SwapVertIcon />
                        ))}
                    </Button>
                  </div>
                </th>
              ))}
              {rowActions && (
                <th
                  className="z-1 w-10 bg-main-surface-lv-02-dim"
                  style={pinRowActions ? pinnedRowActionsStyles : undefined}
                />
              )}
            </tr>
          ))}
        </thead>
        <tbody className="text-left text-ui-body-b1 leading-ui-body-b1 font-medium text-main-text-lv01 **:[td]:h-18 **:[td]:p-2 **:[td]:align-top">
          {table.getRowModel().rows.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length + (hideIndex ? 0 : 1) + (enableRowSelection ? 1 : 0) + (rowActions ? 1 : 0)}
                className="border-b-0 px-4 py-12 align-middle! text-center text-ui-body-b1 leading-ui-body-b1 font-medium text-main-text-lv02"
              >
                <div className="flex h-full flex-col justify-center gap-2 text-center">
                  {typeof emptyMessage === 'object' && (
                    <span className="text-ui-title-t2 leading-ui-title-t2 font-semibold text-main-text-lv01">
                      {emptyMessage.title}
                    </span>
                  )}
                  <span className="whitespace-pre-line text-ui-body-b2 leading-ui-body-b2 font-medium text-main-text-lv02">
                    {typeof emptyMessage === 'string' ? emptyMessage : emptyMessage.description}
                  </span>
                </div>
              </td>
            </tr>
          ) : (
            table.getRowModel().rows.map((row, rowIndex) => (
              <tr
                key={row.id}
                className={cn(
                  'group transition-colors hover:bg-creation-neutral-container-surface-pressed',
                  handleRowClick ? 'cursor-pointer' : '',
                )}
                onClick={handleRowClick ? () => handleRowClick(row.original) : undefined}
              >
                {!hideIndex && (
                  <td
                    className={cn(
                      'w-10 border-b border-main-text-lv03 text-main-text-lv02',
                      rowIndex === table.getRowModel().rows.length - 1 && 'border-b-0',
                    )}
                  >
                    {pagination ? (pagination.page - 1) * pagination.pageSize + rowIndex + 1 : rowIndex + 1}
                  </td>
                )}
                {enableRowSelection && (
                  <td
                    className={cn(
                      'w-14 border-b border-main-text-lv03',
                      rowIndex === table.getRowModel().rows.length - 1 && 'border-b-0',
                    )}
                    onClick={(event) => event.stopPropagation()}
                  >
                    <Checkbox
                      checked={row.getIsSelected()}
                      onCheckedChange={(checked) => row.toggleSelected(Boolean(checked))}
                      aria-label="Select row"
                    />
                  </td>
                )}
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className={cn(
                      'transition-colors border-b border-main-text-lv03 text-main-text-lv01',
                      rowIndex === table.getRowModel().rows.length - 1 && 'border-b-0',
                      cell.column.getIsPinned()
                        ? 'bg-creation-neutral-container-surface-default group-hover:bg-creation-neutral-container-surface-pressed'
                        : '',
                      cell.column.getIsPinned() === 'left' && cell.column.getIsLastColumn('left')
                        ? "after:content-[''] after:absolute after:right-0 after:top-0 after:bottom-0 after:w-px after:bg-main-border-lv02 after:z-10"
                        : '',
                      cell.column.getIsPinned() === 'right' && cell.column.getIsFirstColumn('right')
                        ? "after:content-[''] after:absolute after:left-0 after:top-0 after:bottom-0 after:w-px after:bg-main-border-lv02 after:z-10"
                        : '',
                      cell.column.columnDef.meta?.cellClassName,
                    )}
                    style={getCommonPinningStyles(cell.column, columns, rightPinningOffset)}
                  >
                    <div className="px-1.5 py-1 wrap-break-word">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </div>
                  </td>
                ))}
                {rowActions && (
                  <td
                    className={cn(
                      'transition-colors w-10 border-b border-main-text-lv03 text-center',
                      rowIndex === table.getRowModel().rows.length - 1 && 'border-b-0',
                      pinRowActions &&
                        'z-1 bg-creation-neutral-container-surface-default group-hover:bg-creation-neutral-container-surface-pressed has-[[data-slot=row-actions-menu]]:z-50',
                    )}
                    style={pinRowActions ? pinnedRowActionsStyles : undefined}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <RowActionsMenu
                      actions={rowActions(row.original)}
                      popoverContentClassName={popoverContentClassName}
                    />
                  </td>
                )}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  )

  const paginationEl = pagination && table.getRowModel().rows.length > 0 && (
    <Pagination
      page={pagination.page}
      totalPages={pagination.totalPages}
      totalItems={pagination.totalItems}
      pageSize={pagination.pageSize}
      onPageChange={pagination.onPageChange}
      showGoToInput={pagination.showGoToInput}
      showResultLabel={pagination.showResultLabel}
    />
  )

  return (
    <div className={cn('flex flex-col gap-4', data.length === 0 && 'flex-1', className)}>
      {tableContent}
      {paginationEl}
    </div>
  )
}

export function Pagination({
  page,
  totalPages,
  totalItems,
  pageSize,
  onPageChange,
  showGoToInput = true,
  showResultLabel,
}: {
  page: number
  totalPages: number
  totalItems: number
  pageSize: number
  onPageChange: (page: number) => void
  showGoToInput?: boolean
  showResultLabel?: boolean
}) {
  const start = (page - 1) * pageSize + 1
  const end = Math.min(page * pageSize, totalItems)

  const getPages = (): (number | '...')[] => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1)
    }
    const pages: (number | '...')[] = []
    const delta = 2
    const left = Math.max(2, page - delta)
    const right = Math.min(totalPages - 1, page + delta)

    pages.push(1)
    if (left > 2) pages.push('...')
    for (let i = left; i <= right; i++) pages.push(i)
    if (right < totalPages - 1) pages.push('...')
    pages.push(totalPages)

    return pages
  }

  return (
    <div className="flex flex-wrap items-center justify-end gap-6">
      <span className="w-36 shrink-0 text-ui-body-b2 leading-ui-body-b2 font-medium text-main-text-lv02">
        {showResultLabel !== false
          ? `Result ${end === 0 ? 0 : start + '-' + end} out of ${totalItems}`
          : `${end === 0 ? 0 : start + '-' + end} of ${totalItems}`}
      </span>
      <div className="flex max-w-full flex-wrap items-center justify-end gap-1">
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
          aria-label="Previous page"
        >
          <ChevronLeftIcon className="size-4 text-main-text-lv02" aria-hidden="true" />
        </Button>
        {getPages().map((p, i) =>
          p === '...' ? (
            <span
              key={`ellipsis-${i}`}
              className="flex h-8 w-8 items-center justify-center text-comp-button-bt2 leading-comp-button-bt2 font-semibold text-main-text-lv02"
            >
              ...
            </span>
          ) : (
            <Button
              key={p}
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onPageChange(p)}
              aria-current={p === page ? 'page' : undefined}
              className={cn(
                p === page
                  ? 'text-comp-button-bt2 leading-comp-button-bt2 font-semibold text-creation-primary-container-on-surface-focused'
                  : 'text-comp-button-bt2 leading-comp-button-bt2 font-semibold text-main-text-lv02 focus:text-main-text-lv01',
              )}
            >
              {p}
            </Button>
          ),
        )}
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          disabled={page >= totalPages}
          onClick={() => onPageChange(page + 1)}
          aria-label="Next page"
        >
          <ChevronRightIcon className="size-4 text-main-text-lv02" aria-hidden="true" />
        </Button>
      </div>
      {showGoToInput && (
        <div className="flex shrink-0 items-center gap-2 [&>div]:mt-0">
          <span className="text-ui-body-b2 leading-ui-body-b2 font-medium text-main-text-lv02">Go to :</span>
          <SimpleSelect
            value={String(page)}
            onValueChange={(value) => {
              const nextPage = Number(value)
              if (Number.isInteger(nextPage) && nextPage >= 1 && nextPage <= totalPages) {
                onPageChange(nextPage)
              }
            }}
            items={Array.from({ length: totalPages }, (_, index) => ({
              value: String(index + 1),
              label: String(index + 1),
            }))}
            floatingLabel={false}
            aria-label="Go to page"
            className="h-10.5 w-20 font-normal"
          />
        </div>
      )}
    </div>
  )
}

export function RowActionsMenu({
  actions,
  popoverContentClassName,
}: {
  actions: RowAction[]
  popoverContentClassName?: string
}) {
  const [open, setOpen] = useState(false)
  const [menuPos, setMenuPos] = useState<{ top: number; right: number }>({
    top: 0,
    right: 0,
  })
  const triggerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (triggerRef.current && !triggerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  if (actions.length === 0) return null

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!open) {
      const rect = triggerRef.current?.getBoundingClientRect()
      if (rect) {
        setMenuPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right })
      }
    }
    setOpen(!open)
  }

  return (
    <div ref={triggerRef} className="relative">
      <button
        type="button"
        onClick={handleToggle}
        className="flex size-8 items-center justify-center rounded-lg transition-colors"
        aria-label="More actions"
      >
        <MoreVertical size={16} className="text-creation-secondary-container-on-surface-default" />
      </button>
      {open && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={(e) => {
              e.stopPropagation()
              setOpen(false)
            }}
          />
          <div
            data-slot="row-actions-menu"
            className={cn(
              'fixed z-50 min-w-28 rounded-300 bg-main-surface-lv-02 p-2 shadow-elevation-lv03',
              popoverContentClassName,
            )}
            style={{ top: menuPos.top, right: menuPos.right }}
            onClick={(e) => e.stopPropagation()}
          >
            {actions.map((action) => (
              <div key={action.label}>
                {action.dividerBefore && <hr className="my-1 border-main-text-lv03" />}
                <ListMenuItem
                  label={action.label}
                  prependIcon={action.icon}
                  disabled={action.disabled}
                  onClick={() => {
                    action.onClick()
                    setOpen(false)
                  }}
                />
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    className?: string
    cellClassName?: string
  }
}
